import { type OrderLineDiscountRecord, OrderLineDiscountSource } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiAsyncConfirmationDialog, NoddiIconButton } from 'noddi-ui-web';
import { canDeleteDiscount, canEditDiscount } from 'noddi-util';
import { useState } from 'react';

import { EditDiscountDialog } from './EditDiscountDialog';

// remove_discount takes a coupon or membership discount off every line it covers.
const DELETE_DESCRIPTION: Record<OrderLineDiscountSource, string> = {
  [OrderLineDiscountSource.COUPON]:
    'The coupon goes back to the customer and comes off every line it discounts on this order.',
  [OrderLineDiscountSource.MEMBERSHIP]: 'The membership discount comes off every line it discounts on this order.',
  [OrderLineDiscountSource.MANUAL]: 'Are you sure you want to delete this discount?',
  [OrderLineDiscountSource.AUTOMATIC]: 'Are you sure you want to delete this discount?'
};

type Props = {
  discount: OrderLineDiscountRecord;
  orderLineFrozenAt?: string | null;
  onSuccess: () => void;
  /** When provided, the edit button delegates to this instead of opening its own dialog (avoids stacked modals). */
  onEdit?: (discount: OrderLineDiscountRecord) => void;
};

export const DiscountLineActions = ({ discount, orderLineFrozenAt, onSuccess, onEdit }: Props) => {
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { mutateAsync: removeDiscount, isPending: isRemoving } = noddiAsync.usePost({
    type: URLKeys.removeOrderLineDiscount,
    queryConfig: { onSuccess }
  });

  const editable = canEditDiscount(discount, orderLineFrozenAt);
  const deletable = canDeleteDiscount(discount, orderLineFrozenAt);

  // A campaign or frozen discount can be neither edited nor deleted; showing the
  // controls greyed out only offers a click that goes nowhere.
  if (!editable && !deletable) {
    return null;
  }

  return (
    <div className='flex shrink-0 items-center gap-1'>
      {editable && (
        <NoddiIconButton
          iconName='Edit'
          variant='ghost'
          size='small'
          onClick={() => (onEdit ? onEdit(discount) : setIsEditOpen(true))}
        />
      )}
      {deletable && (
        <NoddiAsyncConfirmationDialog
          title='Delete discount'
          description={DELETE_DESCRIPTION[discount.source]}
          trigger={<NoddiIconButton iconName='Trash2' variant='ghost' size='small' className='text-destructive' />}
          confirmActionLoading={isRemoving}
          confirmAction={async () => {
            await removeDiscount({ discountId: discount.id });
          }}
        />
      )}
      {!onEdit && isEditOpen && (
        <EditDiscountDialog
          discountId={discount.id}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            onSuccess();
            setIsEditOpen(false);
          }}
        />
      )}
    </div>
  );
};
